import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COMPANY_INFO } from '../../constants/companyInfo';
import { Save, Plus, Trash2, Download, Wallet, ArrowRightLeft, Hash, Phone, AlertCircle } from 'lucide-react';

// ==========================================
// 100% FLEXIBLE SEARCHABLE SELECT WITH LOGS
// ==========================================
const SearchableSelect = ({ value, onChange, placeholder, subSubHeadFilter, restrictToId, isTable = false, filterCash = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    const getAccountData = async (queryStr) => {
        try {
            const res = await API.get(`/vouchers/search?${queryStr}`);
            return res.data?.data || res.data || [];
        } catch (err) {
            console.error("Search API Call Error: ", err);
            return [];
        }
    };

    useEffect(() => {
        if (value && typeof value !== 'object') {
            getAccountData(`search=${value}`).then(data => {
                const found = data.find(a => a.id == value);
                if (found) {
                    setSearchTerm(found.title);
                }
            });
        } else if (!value) { 
            setSearchTerm(''); 
        }
    }, [value]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (restrictToId) {
                    const data = await getAccountData(`search=${restrictToId}`);
                    const found = data.filter(a => a.id == restrictToId) || [];
                    setOptions(found);
                    return;
                }

                let queryStr = `search=${encodeURIComponent(searchTerm)}&limit=200`;
                
                if (subSubHeadFilter) {
                    queryStr += `&SubSubHeadID=${encodeURIComponent(subSubHeadFilter)}`;
                }

                let rawData = await getAccountData(queryStr);

                if (!Array.isArray(rawData)) rawData = [];

                let filteredData = rawData;
                
                if (subSubHeadFilter) {
                    filteredData = filteredData.filter(a => {
                        const accSubHead = String(a.SubSubHeadID || a.sub_sub_head_id || '').trim();
                        const filterVal = String(subSubHeadFilter).trim();
                        return accSubHead === filterVal;
                    });
                }
                
                if (filterCash) {
                    filteredData = filteredData.filter(a => {
                        const titleLower = (a.title || '').toLowerCase();
                        return !titleLower.includes('cash');
                    });
                }

                setOptions(filteredData);
            } catch (err) {
                console.error("Suggestions fetch failed: ", err);
            }
        };

        if (isOpen) {
            const delayDebounce = setTimeout(fetchData, 150);
            return () => clearTimeout(delayDebounce);
        }
    }, [searchTerm, isOpen, subSubHeadFilter, restrictToId, filterCash]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <input
                type="text"
                style={isTable ? styles.inputTable : styles.inputMain}
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => { 
                    setSearchTerm(e.target.value); 
                    setIsOpen(true); 
                }}
                onFocus={() => setIsOpen(true)}
            />
            {isOpen && options.length > 0 && (
                <div style={styles.dropdownList}>
                    {options.map((opt, idx) => (
                        <div key={opt.id || idx} style={styles.dropdownItem} onMouseDown={() => { 
                            onChange(opt); 
                            setSearchTerm(opt.title); 
                            setIsOpen(false); 
                        }}>
                            <div style={{ fontWeight: '700', color: '#1c313a', fontSize: '13px' }}>{opt.title}</div>
                            <div style={{ fontSize: '10px', color: '#78909c' }}>
                                ID: {opt.id} | SubSubHeadID: {opt.SubSubHeadID || opt.sub_sub_head_id || 'N/A'} {opt.phone ? `| Ph: ${opt.phone}` : ''}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==========================================
// MAIN VOUCHER SYSTEM COMPLETE COMPONENT
// ==========================================
const VoucherSystem = () => {
    const [loading, setLoading] = useState(false);
    
    const [systemIds] = useState({ 
        cash_sub_sub_head_id: '521000', 
        bank_sub_sub_head_id: '522000'
    });

    const [selectedControlAcc, setSelectedControlAcc] = useState(null);
    const [header, setHeader] = useState({
        voucher_type: 'CPV',
        transaction_date: new Date().toISOString().split('T')[0],
        cash_account_id: '',
        sub_sub_head_filter: '521000',
        narration: '' // Added narration field to prevent hardcoded text
    });

    const [entries, setEntries] = useState([{ 
        account_id: '', 
        account_detail: null, 
        dr_account_id: '', 
        dr_account_detail: null, 
        cr_account_id: '', 
        cr_account_detail: null, 
        invoice_no: '', 
        remarks: '', 
        amount: '' 
    }]);

    const fetchFirstAccountBySubHead = async (subSubHeadId) => {
        try {
            const res = await API.get(`/vouchers/search?SubSubHeadID=${subSubHeadId}&limit=1`);
            const dataList = res.data?.data || res.data || [];
            return dataList.length > 0 ? dataList[0] : null;
        } catch (err) {
            console.error("Control Account Fetch Error: ", err);
            return null;
        }
    };

    useEffect(() => {
        const loadInitialControl = async () => {
            if (header.voucher_type === 'CPV' || header.voucher_type === 'CRV') {
                const targetSubHead = systemIds.cash_sub_sub_head_id;
                setHeader(prev => ({ ...prev, cash_account_id: '', sub_sub_head_filter: targetSubHead }));
                
                try {
                    const cashAcc = await fetchFirstAccountBySubHead(targetSubHead);
                    if (cashAcc) {
                        setHeader(prev => ({ ...prev, cash_account_id: cashAcc.id, sub_sub_head_filter: targetSubHead }));
                        setSelectedControlAcc(cashAcc);
                    } else {
                        setSelectedControlAcc(null);
                    }
                } catch (err) {
                    console.error("Error setting initial cash account: ", err);
                }
            } else if (header.voucher_type === 'BPV' || header.voucher_type === 'BRV') {
                const targetSubHead = systemIds.bank_sub_sub_head_id;
                setHeader(prev => ({ ...prev, cash_account_id: '', sub_sub_head_filter: targetSubHead }));
                setSelectedControlAcc(null);
            } else {
                setHeader(prev => ({ ...prev, cash_account_id: '', sub_sub_head_filter: '' }));
                setSelectedControlAcc(null);
            }

            if (header.voucher_type === 'JV') {
                setEntries([{ dr_account_id: '', dr_account_detail: null, cr_account_id: '', cr_account_detail: null, invoice_no: '', remarks: '', amount: '' }]);
            } else {
                setEntries([{ account_id: '', account_detail: null, invoice_no: '', remarks: '', amount: '' }]);
            }
        };

        loadInitialControl();
    }, [header.voucher_type, systemIds]);

    const totalAmount = entries.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

    const updateEntry = (index, field, value, detailValue = null) => {
        const newEntries = [...entries];
        newEntries[index][field] = value;
        if (detailValue !== null) {
            newEntries[index][field + '_detail'] = detailValue;
        }
        setEntries(newEntries);
    };

    const getFlowDirectionText = () => {
        if (header.voucher_type === 'CPV') return "Paying Out: From Cash Control Account ➔ To Multiple Ledger Accounts Below";
        if (header.voucher_type === 'BPV') return "Paying Out: From Bank Control Account ➔ To Multiple Ledger Accounts Below";
        if (header.voucher_type === 'CRV') return "Receiving In: From Multiple Ledger Accounts Below ➔ Into Cash Control Account";
        if (header.voucher_type === 'BRV') return "Receiving In: From Multiple Ledger Accounts Below ➔ Into Bank Control Account";
        return "Journal Adjustment: Direct balance shifting between ledgers (Debit to Credit)";
    };

    const exportPDF = () => {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 15;

        const companyName = COMPANY_INFO?.name || "BIKE INVENTORY SYSTEM";
        const companyTagline = COMPANY_INFO?.tagline || "Deals in All Kinds of Bikes & Spare Parts";
        const companyAddress = COMPANY_INFO?.address || "Main Road, Pakistan";
        const companyPhone = COMPANY_INFO?.phone || "N/A";
        const companyEmail = COMPANY_INFO?.email || "N/A";

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(44, 62, 80); 
        const splitCompanyName = doc.splitTextToSize(companyName, pageWidth - 100);
        doc.text(splitCompanyName, margin, 18);
        
        let nextY = 18 + (splitCompanyName.length * 6);

        doc.setFontSize(8.5);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(127, 140, 141); 
        doc.text(companyTagline, margin, nextY);
        nextY += 5;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(52, 73, 94);
        doc.text(companyAddress, margin, nextY);
        nextY += 5;
        doc.text(`Phone: ${companyPhone} | Email: ${companyEmail}`, margin, nextY);

        const metaBoxX = pageWidth - 80;
        const metaBoxY = 14;
        const metaBoxW = 65;
        const metaBoxH = 26;

        doc.setFillColor(245, 247, 250);
        doc.roundedRect(metaBoxX, metaBoxY, metaBoxW, metaBoxH, 2, 2, 'F');
        doc.setDrawColor(220, 224, 230);
        doc.setLineWidth(0.3);
        doc.roundedRect(metaBoxX, metaBoxY, metaBoxW, metaBoxH, 2, 2, 'D');

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(22, 160, 133); 
        doc.text(`${header.voucher_type} VOUCHER`, metaBoxX + 5, metaBoxY + 6);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(44, 62, 80);
        doc.text(`Date: ${header.transaction_date}`, metaBoxX + 5, metaBoxY + 12);
        
        let controlAccName = selectedControlAcc ? `${selectedControlAcc.title}` : (header.voucher_type === 'JV' ? 'Journal Entry' : 'N/A');
        const splitControl = doc.splitTextToSize(`Control: ${controlAccName}`, metaBoxW - 10);
        doc.text(splitControl, metaBoxX + 5, metaBoxY + 18);

        doc.setDrawColor(22, 160, 133);
        doc.setLineWidth(0.8);
        doc.line(margin, 44, pageWidth - margin, 44);

        const tableBody = entries.map(e => {
            let accountTitle = '-';
            if (header.voucher_type === 'JV') {
                accountTitle = `Dr: ${e.dr_account_detail?.title || 'N/A'} \nCr: ${e.cr_account_detail?.title || 'N/A'}`;
            } else {
                accountTitle = e.account_detail?.title || '-';
            }
            return [
                e.invoice_no || '-',
                accountTitle,
                e.remarks || '-', 
                { content: Number(e.amount).toLocaleString(undefined, {minimumFractionDigits: 2}), styles: { halign: 'right' } }
            ];
        });

        autoTable(doc, {
            startY: 48,
            margin: { left: margin, right: margin },
            head: [['REF / INV', 'ACCOUNT TITLE', 'PARTICULARS / REMARKS', 'AMOUNT (PKR)']],
            body: tableBody,
            theme: 'striped',
            headStyles: { 
                fillColor: [44, 62, 80], 
                textColor: [255, 255, 255], 
                fontSize: 9, 
                fontStyle: 'bold',
                halign: 'left',
                cellPadding: 3.5
            },
            styles: { 
                fontSize: 8.5, 
                cellPadding: 4, 
                valign: 'middle',
                overflow: 'linebreak' 
            },
            columnStyles: {
                0: { cellWidth: 22 },  
                1: { cellWidth: 58 },  
                2: { cellWidth: 70 },  
                3: { cellWidth: 30, halign: 'right' }   
            },
            foot: [[
                { content: 'Grand Total in Words: ' + totalAmount.toLocaleString() + ' PKR Only', colSpan: 2, styles: { fontSize: 7.5, fontStyle: 'italic', textColor: [100, 100, 100] } },
                { content: 'Total', styles: { halign: 'right', fontStyle: 'bold', fontSize: 9 } },
                { content: totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2}), styles: { halign: 'right', fontStyle: 'bold', fontSize: 9, textColor: [22, 160, 133] } }
            ]],
            footStyles: { fillColor: [245, 247, 250], textColor: [44, 62, 80], cellPadding: 3.5 }
        });

        const finalY = doc.lastAutoTable.finalY + 30;
        doc.setDrawColor(189, 195, 199);
        doc.setLineWidth(0.3);
        
        doc.line(margin, finalY, margin + 45, finalY);
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "normal");
        doc.text("Prepared By", margin, finalY + 5);

        doc.line(pageWidth - margin - 45, finalY, pageWidth - margin, finalY);
        doc.text("Authorized Signature", pageWidth - margin - 45, finalY + 5);

        doc.save(`${header.voucher_type}_${header.transaction_date}.pdf`);
    };

    const handleSubmit = async () => {
        if (header.voucher_type !== 'JV' && !header.cash_account_id) return alert("Select Control Account!");
        setLoading(true);
        try {
            // Hardcoded "Posted via Multi-UI System" khatam kar ke ab narration ya dynamic description pass ho rahi hai
            const res = await API.post('/vouchers/multi', { 
                ...header, 
                entries, 
                description: header.narration || entries[0]?.remarks || `${header.voucher_type} Entry` 
            });
            if (res.data.success) {
                exportPDF(); 
                alert("Voucher Posted Successfully!");
            }
        } catch (err) { 
            console.error(err);
            alert("Posting Error details: " + (err.response?.data?.error || err.message)); 
        } finally { setLoading(false); }
    };

    const voucherTypes = ['CPV', 'CRV', 'BPV', 'BRV', 'JV'];

    return (
        <div style={styles.container}>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            {loading && (
                <div style={loaderStyles.overlay}>
                    <div style={loaderStyles.container}>
                        <div style={loaderStyles.spinner}></div>
                        <p style={loaderStyles.text}>Posting Voucher...</p>
                        <p style={loaderStyles.subText}>Please wait while we process your transaction</p>
                    </div>
                </div>
            )}
            <div style={styles.mainWrapper}>
                
                {/* --- TOP HEADER NAVIGATION BAR --- */}
                <div style={styles.topSection}>
                    <div style={styles.brandGroup}>
                        <div style={styles.iconBox}><Wallet size={22} color="#fff" /></div>
                        <h2 style={styles.pageTitle}>Voucher System</h2>
                    </div>
                    
                    <div style={styles.actionGroup}>
                        <div style={styles.buttonSegment}>
                            {voucherTypes.map((type) => {
                                const isActive = header.voucher_type === type;
                                return (
                                    <button 
                                        key={type}
                                        type="button"
                                        style={isActive ? styles.segmentedBtnActive : styles.segmentedBtn}
                                        onClick={() => setHeader({ ...header, voucher_type: type })}
                                    >
                                        {type}
                                    </button>
                                );
                            })}
                        </div>
                        <input type="date" style={styles.dateTop} value={header.transaction_date} onChange={e => setHeader({...header, transaction_date: e.target.value})} />
                    </div>
                </div>

                {/* --- RUNNING WORKFLOW EXPLANATOR --- */}
                <div style={styles.flowBar}>
                    <span style={{ marginRight: '6px' }}>🔄</span> {getFlowDirectionText()}
                </div>

                {/* --- DYNAMIC HEADER CONTROL ACCOUNT SELECTOR --- */}
                <div style={styles.controlBox}>
                    {header.voucher_type !== 'JV' && (
                        <div style={{ flex: 1.5 }}>
                            <label style={styles.sectionLabel}>
                                {header.voucher_type.startsWith('C') ? 'Cash Control Account (SubSubHead: 521000)' : 'Bank Control Account (SubSubHead: 522000)'}
                            </label>
                            <SearchableSelect 
                                value={header.cash_account_id} 
                                subSubHeadFilter={header.sub_sub_head_filter}
                                filterCash={false} 
                                placeholder={header.voucher_type.startsWith('C') ? "Search Cash Accounts..." : "Search Bank Accounts..."} 
                                onChange={acc => { setHeader({...header, cash_account_id: acc.id}); setSelectedControlAcc(acc); }} 
                            />
                        </div>
                    )}
                    <div style={{ flex: 1.5 }}>
                        <label style={styles.sectionLabel}>General Voucher Narration / Description</label>
                        <input 
                            type="text" 
                            style={styles.inputMain} 
                            placeholder="Optional general narration for voucher..." 
                            value={header.narration}
                            onChange={e => setHeader({...header, narration: e.target.value})}
                        />
                    </div>
                    {header.voucher_type !== 'JV' && (
                        <div style={selectedControlAcc ? styles.detailCardActive : styles.detailCardEmpty}>
                            {selectedControlAcc ? (
                                <div style={styles.detailGrid}>
                                    <div style={{gridColumn: 'span 2', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderBottomColor: '#eee', paddingBottom: '4px'}}>
                                        <b style={{fontSize: '14px', color: '#1c313a'}}>{selectedControlAcc.title}</b>
                                    </div>
                                    <div style={styles.detailItem}><Phone size={12}/> {selectedControlAcc.phone || 'No Phone Registered'}</div>
                                    <div style={styles.detailItem}><Hash size={12}/> SubSubHeadID: {selectedControlAcc.SubSubHeadID || selectedControlAcc.sub_sub_head_id || 'N/A'}</div>
                                </div>
                            ) : <div style={styles.emptyMsg}><AlertCircle size={18}/> Select Control Account</div>}
                        </div>
                    )}
                </div>

                {/* --- MAIN ENTRY WORKSPACE TABLE --- */}
                <div style={styles.tableArea}>
                    <table style={styles.mainTable}>
                        <thead>
                            {header.voucher_type === 'JV' ? (
                                <tr style={styles.tableHead}>
                                    <th style={{width: '10%'}}>Ref</th>
                                    <th style={{width: '25%'}}>Debit Account (Dr)</th>
                                    <th style={{width: '25%'}}>Credit Account (Cr)</th>
                                    <th style={{width: '20%'}}>Narration / Remarks</th>
                                    <th style={{width: '15%', textAlign: 'right'}}>Amount (PKR)</th>
                                    <th style={{width: '5%'}}></th>
                                </tr>
                            ) : (
                                <tr style={styles.tableHead}>
                                    <th style={{width: '10%'}}>Ref</th>
                                    <th style={{width: '35%'}}>Account Title (Any Ledger)</th>
                                    <th style={{width: '30%'}}>Narration / Remarks</th>
                                    <th style={{width: '20%', textAlign: 'right'}}>Amount (PKR)</th>
                                    <th style={{width: '5%'}}></th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {entries.map((item, index) => (
                                <React.Fragment key={index}>
                                    {header.voucher_type === 'JV' ? (
                                        <tr>
                                            <td>
                                                <input 
                                                    style={styles.inputTable} 
                                                    value={item.invoice_no} 
                                                    placeholder="Ref" 
                                                    onChange={e => updateEntry(index, 'invoice_no', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <SearchableSelect 
                                                    isTable 
                                                    value={item.dr_account_id} 
                                                    placeholder="Search Debit Account..." 
                                                    onChange={acc => updateEntry(index, 'dr_account_id', acc.id, acc)} 
                                                />
                                            </td>
                                            <td>
                                                <SearchableSelect 
                                                    isTable 
                                                    value={item.cr_account_id} 
                                                    placeholder="Search Credit Account..." 
                                                    onChange={acc => updateEntry(index, 'cr_account_id', acc.id, acc)} 
                                                />
                                            </td>
                                            <td>
                                                <textarea 
                                                    rows={1}
                                                    style={styles.textareaTable} 
                                                    value={item.remarks} 
                                                    placeholder="Enter Remarks/Particulars..." 
                                                    onChange={e => updateEntry(index, 'remarks', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <input 
                                                    type="number" 
                                                    style={styles.amountInput} 
                                                    value={item.amount} 
                                                    placeholder="0.00" 
                                                    onChange={e => updateEntry(index, 'amount', e.target.value)} 
                                                />
                                            </td>
                                            <td style={{textAlign:'center'}}>
                                                <button 
                                                    onClick={() => setEntries(entries.filter((_, i) => i !== index))} 
                                                    style={styles.delBtn}
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr>
                                            <td>
                                                <input 
                                                    style={styles.inputTable} 
                                                    value={item.invoice_no} 
                                                    placeholder="Ref" 
                                                    onChange={e => updateEntry(index, 'invoice_no', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <SearchableSelect 
                                                    isTable 
                                                    value={item.account_id} 
                                                    placeholder="Search Any Account..." 
                                                    onChange={acc => updateEntry(index, 'account_id', acc.id, acc)} 
                                                />
                                            </td>
                                            <td>
                                                <textarea 
                                                    rows={1}
                                                    style={styles.textareaTable} 
                                                    value={item.remarks} 
                                                    placeholder="Enter Remarks/Particulars..." 
                                                    onChange={e => updateEntry(index, 'remarks', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <input 
                                                    type="number" 
                                                    style={styles.amountInput} 
                                                    value={item.amount} 
                                                    placeholder="0.00" 
                                                    onChange={e => updateEntry(index, 'amount', e.target.value)} 
                                                />
                                            </td>
                                            <td style={{textAlign:'center'}}>
                                                <button 
                                                    onClick={() => setEntries(entries.filter((_, i) => i !== index))} 
                                                    style={styles.delBtn}
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                    
                                    {header.voucher_type !== 'JV' && item.account_detail && (
                                        <tr>
                                            <td colSpan={header.voucher_type === 'JV' ? "6" : "5"}>
                                                <div style={styles.rowInfo}>
                                                    <ArrowRightLeft size={12} />
                                                    <b>{item.account_detail.title}</b> | SubSubHeadID: {item.account_detail.SubSubHeadID || item.account_detail.sub_sub_head_id || 'N/A'} {item.account_detail.phone ? `| Ph: ${item.account_detail.phone}` : ''}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    <button style={styles.addBtn} onClick={() => setEntries([...entries, header.voucher_type === 'JV' ? { dr_account_id: '', dr_account_detail: null, cr_account_id: '', cr_account_detail: null, invoice_no: '', remarks: '', amount: '' } : { account_id: '', account_detail: null, invoice_no: '', remarks: '', amount: '' }])}>
                        <Plus size={14} /> Add Row
                    </button>
                </div>

                {/* --- FOOTER TOTALS & ACTION TRIGGER BAR --- */}
                <div style={styles.footerSection}>
                    <div>
                        <span style={styles.totalLabel}>Grand Total</span>
                        <h2 style={styles.grandTotal}>{totalAmount.toLocaleString()} <small style={{fontSize:'14px'}}>PKR</small></h2>
                    </div>
                    <div style={{display:'flex', gap:'12px'}}>
                        <button style={styles.btnSecondary} onClick={exportPDF}>
                            <Download size={18} /> Preview PDF
                        </button>
                        <button style={styles.postBtn} onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Posting...' : 'Post & Print'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: { background: '#eef2f3', minHeight: '100vh', padding: '30px' },
    mainWrapper: { maxWidth: '1300px', margin: '0 auto', background: '#fff', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', overflow: 'hidden' },
    topSection: { background: '#1c313a', padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' },
    brandGroup: { display: 'flex', alignItems: 'center', gap: '12px' },
    iconBox: { background: '#37474f', padding: '8px', borderRadius: '10px' },
    pageTitle: { margin: 0, fontSize: '18px', fontWeight: '800' },
    actionGroup: { display: 'flex', gap: '15px', alignItems: 'center' },
    buttonSegment: { display: 'flex', background: '#263238', borderRadius: '8px', padding: '4px', border: '1px solid #455a64' },
    segmentedBtn: { border: 'none', background: 'transparent', color: '#b0bec5', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s ease' },
    segmentedBtnActive: { border: 'none', background: '#1c313a', color: '#fff', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' },
    flowBar: { background: '#eceff1', color: '#37474f', padding: '10px 30px', fontSize: '12px', fontWeight: '700', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderBottomColor: '#cfd8dc' },
    dateTop: { background: '#263238', color: '#fff', border: '1px solid #455a64', padding: '10px', borderRadius: '8px', outline: 'none' },
    controlBox: { padding: '20px 30px', display: 'flex', gap: '20px', background: '#f8fafc', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderBottomColor: '#e2e8f0', alignItems: 'center' },
    sectionLabel: { display: 'block', fontSize: '11px', fontWeight: '800', color: '#546e7a', textTransform: 'uppercase', marginBottom: '5px' },
    inputMain: { width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '10px', outline: 'none' },
    detailCardActive: { flex: 1, background: '#fff', borderStyle: 'solid', borderWidth: '1px', borderColor: '#c8e6c9', borderRadius: '12px', padding: '10px 15px' },
    detailCardEmpty: { flex: 1, borderStyle: 'dashed', borderWidth: '2px', borderColor: '#cbd5e1', borderRadius: '12px', padding: '10px', textAlign: 'center', color: '#94a3b8' },
    detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' },
    detailItem: { fontSize: '11px', color: '#546e7a', display: 'flex', alignItems: 'center', gap: '4px' },
    emptyMsg: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px' },
    tableArea: { padding: '20px 30px' },
    mainTable: { width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' },
    tableHead: { textAlign: 'left', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' },
    inputTable: { width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none' },
    textareaTable: { width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', minHeight: '38px', fontFamily: 'inherit' },
    amountInput: { width: '100%', padding: '10px', border: '2px solid #cbd5e1', borderRadius: '8px', textAlign: 'right', fontWeight: '800', background: '#fffbe6', fontSize: '15px' },
    rowInfo: { background: '#f0f9ff', padding: '5px 12px', borderRadius: '6px', fontSize: '10px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '-5px' },
    addBtn: { marginTop: '10px', padding: '8px 16px', background: '#fff', border: '1px solid #1c313a', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' },
    footerSection: { padding: '20px 30px', background: '#fcfcfc', borderTopStyle: 'solid', borderTopWidth: '1px', borderTopColor: '#e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    totalLabel: { fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' },
    grandTotal: { margin: 0, fontSize: '28px', fontWeight: '900', color: '#1c313a' },
    postBtn: { background: '#1c313a', color: '#fff', padding: '12px 30px', borderRadius: '10px', border: 'none', fontWeight: 'bold', cursor: 'pointer' },
    btnSecondary: { background: '#fff', border: '1px solid #cbd5e1', padding: '12px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
    delBtn: { border: 'none', background: 'none', color: '#cbd5e1', cursor: 'pointer' },
    dropdownList: { position: 'absolute', top: '105%', left: 0, width: '100%', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '10px', zIndex: 100, boxShadow: '0 10px 20px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' },
    dropdownItem: { padding: '10px', cursor: 'pointer', borderBottomStyle: 'solid', borderBottomWidth: '1px', borderBottomColor: '#f1f5f9' }
};

const loaderStyles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(28, 49, 58, 0.85)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
    },
    container: {
        backgroundColor: '#fff',
        padding: '40px 60px',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '4px solid #e2e8f0',
        borderTop: '4px solid #1c313a',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    text: {
        margin: 0,
        color: '#1c313a',
        fontSize: '18px',
        fontWeight: '700',
        fontFamily: '"Inter", sans-serif'
    },
    subText: {
        margin: 0,
        color: '#64748b',
        fontSize: '13px',
        fontFamily: '"Inter", sans-serif'
    }
};

export default VoucherSystem;